import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, Plus, Pencil, Search, Users, Clock, CalendarCheck, LayoutDashboard, ListTree, Presentation, Banknote, CreditCard, UserCog, Settings, ScanLine, Eye, EyeOff, AlertTriangle, FileBarChart, ScrollText, Baby, Upload, Mail, ClipboardCheck, Trash2, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { PhotoThumb } from "../components/ImageLightbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { OperationalCounters } from "../components/OperationalCounters";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { api, apiError, money, priceLabel, fileUrl } from "../lib/api";
import { StatusBadge, RegistrationSummary } from "./ConfirmacionPage";
import { PaymentsTab, UsersTab, SettingsTab, EntitlementsPanel, DinnerTab } from "./AdminExtras";
import { DashboardTab, IncidentsTab, AuditTab, ReportsTab, WorkshopsTabPro } from "./AdminPhase3";

// Funciones futuras (Fase 2+): confirmación de pagos, escáner QR, check-in,
// asignación de ujieres, temporizadores de taller, control de asistencia y reportes.
// La navegación de estas funciones se agregará aquí cuando se construyan.

export const AdminLogin = ({ onSuccess, title = "Administración", footerLink = false }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("hz_admin_token", data.access_token);
      onSuccess(data.user);
    } catch (err) {
      setError(apiError(err, "No se pudo iniciar sesión."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-hz-bg px-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 rounded-2xl border border-hz-gray/40 bg-white p-8 shadow-sm" data-testid="admin-login-form">
        <div className="flex flex-col items-center gap-3">
          <Logo variant="title" className="h-10" />
          <h1 className="text-lg font-bold text-hz-navy">{title}</h1>
        </div>
        <div>
          <Label htmlFor="admin-email" className="font-semibold text-hz-ink">Correo electrónico</Label>
          <Input id="admin-email" type="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required autoComplete="username" />
        </div>
        <div>
          <Label htmlFor="admin-password" className="font-semibold text-hz-ink">Contraseña</Label>
          <div className="relative mt-1.5">
            <Input id="admin-password" type={showPass ? "text" : "password"} data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" required autoComplete="current-password" />
            <button type="button" onClick={() => setShowPass((v) => !v)} data-testid="toggle-password-btn" aria-label={showPass ? "Ocultar contraseña" : "Mostrar contraseña"}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-hz-ink/50 hover:text-hz-navy">
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {error && (
          <p className="rounded-lg border border-hz-error/30 bg-hz-error/5 px-3 py-2 text-sm font-medium text-hz-error" data-testid="admin-login-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full bg-hz-navy font-bold text-white hover:bg-hz-blue disabled:opacity-60">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Iniciar sesión
        </Button>
        {footerLink && (
          <Link to="/" data-testid="login-back-home-link" className="block text-center text-sm font-semibold text-hz-navy/70 transition-colors hover:text-hz-cyan">
            Volver al sitio del evento
          </Link>
        )}
      </form>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-hz-gray/40 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-hz-cyan/10">
        <Icon className="h-5 w-5 text-hz-cyan" />
      </div>
      <div>
        <p className="text-2xl font-extrabold text-hz-navy">{value}</p>
        <p className="text-xs font-semibold text-hz-ink/60">{label}</p>
      </div>
    </div>
  </div>
);

const CatalogTab = ({ kind, title, parents, parentLabel }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/catalog/${kind}`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/catalog/${kind}`, { name, parent_id: parents ? parentId || null : null });
      toast.success(`${title} creado`);
      setName("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggle = async (item) => {
    try {
      await api.patch(`/admin/catalog/${kind}/${item.id}`, { active: !item.active });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/catalog/${kind}/${editing.id}`, { name: editName });
      toast.success("Nombre actualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid={`catalog-${kind}`}>
      <form onSubmit={create} className="flex flex-col gap-3 rounded-xl border border-hz-gray/40 bg-white p-4 sm:flex-row sm:items-end">
        {parents && (
          <div className="flex-1">
            <Label className="text-xs font-semibold text-hz-ink">{parentLabel}</Label>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)} required data-testid={`catalog-${kind}-parent-select`}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
              <option value="">Selecciona…</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex-1">
          <Label className="text-xs font-semibold text-hz-ink">Nombre</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid={`catalog-${kind}-name-input`} className="mt-1" placeholder={`Nuevo(a) ${title.toLowerCase()}`} />
        </div>
        <Button type="submit" data-testid={`catalog-${kind}-add-btn`} className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
          <Plus className="mr-1 h-4 w-4" /> Agregar
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <p className="py-8 text-sm text-hz-ink/60" data-testid={`catalog-${kind}-empty`}>Sin elementos. Agrega el primero.</p>
      ) : (
        <div className="mt-4 divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40 bg-white">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`catalog-${kind}-row-${item.id}`}>
              <div>
                <p className={`text-sm font-semibold ${item.active ? "text-hz-ink" : "text-hz-ink/40 line-through"}`}>{item.name}</p>
                {item.parent_name && <p className="text-xs text-hz-ink/50">{item.parent_name}</p>}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => { setEditing(item); setEditName(item.name); }} data-testid={`catalog-${kind}-edit-${item.id}`} className="rounded-md p-1.5 text-hz-navy hover:bg-hz-bg" aria-label={`Editar ${item.name}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <Switch checked={item.active} onCheckedChange={() => toggle(item)} data-testid={`catalog-${kind}-toggle-${item.id}`} aria-label={`Activar o desactivar ${item.name}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">Editar nombre</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="catalog-edit-name-input" />
            <Button onClick={saveEdit} data-testid="catalog-edit-save-btn" className="w-full bg-hz-navy font-bold text-white">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const WorkshopsTab = () => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const emptyForm = { name: "", description: "", speaker_name: "", room_name: "", capacity: 80, price: 0, pending_configuration: true };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/workshops")
      .then((r) => setWorkshops(r.data.workshops || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = {
        name: form.name, description: form.description,
        speaker_name: form.speaker_name || null, room_name: form.room_name || null,
        capacity: Number(form.capacity) || 0, price: Number(form.price) || 0,
        pending_configuration: !!form.pending_configuration,
      };
      if (editing) await api.patch(`/admin/workshops/${editing.id}`, payload);
      else await api.post("/admin/workshops", payload);
      toast.success(editing ? "Taller actualizado" : "Taller creado");
      setEditing(null);
      setCreating(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleStatus = async (w) => {
    try {
      await api.patch(`/admin/workshops/${w.id}`, { status: w.status === "published" ? "draft" : "published" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEdit = (w) => {
    setForm({ name: w.name, description: w.description || "", speaker_name: w.speaker_name || "", room_name: w.room_name || "", capacity: w.capacity, price: w.price, pending_configuration: w.pending_configuration });
    setEditing(w);
  };

  const statusLabels = { draft: "Borrador", published: "Publicado", closed: "Cerrado", cancelled: "Cancelado" };

  return (
    <div data-testid="workshops-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(emptyForm); setCreating(true); }} data-testid="workshop-add-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
          <Plus className="mr-1 h-4 w-4" /> Nuevo taller
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="mt-4 divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40 bg-white">
          {workshops.map((w) => (
            <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`workshop-row-${w.id}`}>
              <div>
                <p className="text-sm font-bold text-hz-ink">{w.name}</p>
                <p className="text-xs text-hz-ink/60">
                  {statusLabels[w.status]} · {w.available}/{w.capacity} lugares · {priceLabel(w.price, w.pending_configuration)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => openEdit(w)} data-testid={`workshop-edit-${w.id}`} className="rounded-md p-1.5 text-hz-navy hover:bg-hz-bg" aria-label={`Editar ${w.name}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink/60">
                  Publicado
                  <Switch checked={w.status === "published"} onCheckedChange={() => toggleStatus(w)} data-testid={`workshop-toggle-${w.id}`} />
                </label>
              </div>
            </div>
          ))}
          {workshops.length === 0 && <p className="px-4 py-8 text-sm text-hz-ink/60">Sin talleres.</p>}
        </div>
      )}

      <Dialog open={creating || !!editing} onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">{editing ? "Editar taller" : "Nuevo taller"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="workshop-form-name" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Descripción</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="workshop-form-description" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Ponente</Label>
                <Input value={form.speaker_name} onChange={(e) => setForm({ ...form, speaker_name: e.target.value })} data-testid="workshop-form-speaker" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Salón</Label>
                <Input value={form.room_name} onChange={(e) => setForm({ ...form, room_name: e.target.value })} data-testid="workshop-form-room" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Cupo</Label>
                <Input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="workshop-form-capacity" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Precio (MXN)</Label>
                <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="workshop-form-price" className="mt-1" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
              <Switch checked={form.pending_configuration} onCheckedChange={(v) => setForm({ ...form, pending_configuration: v })} data-testid="workshop-form-pending" />
              Precio pendiente de configuración
            </label>
            <Button onClick={save} data-testid="workshop-form-save" className="w-full bg-hz-navy font-bold text-white">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PricingTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ amount: 0, pending_configuration: true, description: "", schedule: "", includes: "", min_age: "", max_age: "", capacity: "", requirements_message: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/pricing")
      .then((r) => setItems(r.data.pricing || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    try {
      await api.patch(`/admin/pricing/${item.id}`, { active: !item.active });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async () => {
    try {
      const payload = {
        amount: Number(form.amount) || 0,
        pending_configuration: !!form.pending_configuration,
        description: form.description || null,
        schedule: form.schedule || null,
        includes: form.includes || null,
      };
      if (editing.code === "child_care_fee") {
        payload.min_age = form.min_age === "" ? null : Number(form.min_age);
        payload.max_age = form.max_age === "" ? null : Number(form.max_age);
        payload.capacity = form.capacity === "" ? null : Number(form.capacity);
        payload.requirements_message = form.requirements_message || null;
      }
      await api.patch(`/admin/pricing/${editing.id}`, payload);
      toast.success("Precio actualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="pricing-tab">
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40 bg-white">
          {items.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`pricing-row-${item.code}`}>
              <div>
                <p className="text-sm font-bold text-hz-ink">{item.name}</p>
                <p className="text-xs text-hz-ink/60">{priceLabel(item.amount, item.pending_configuration)} · {item.active ? "Activo" : "Inactivo"}</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => { setEditing(item); setForm({ amount: item.amount, pending_configuration: item.pending_configuration, description: item.description || "", schedule: item.schedule || "", includes: item.includes || "", min_age: item.min_age ?? "", max_age: item.max_age ?? "", capacity: item.capacity ?? "", requirements_message: item.requirements_message || "" }); }} data-testid={`pricing-edit-${item.code}`} className="rounded-md p-1.5 text-hz-navy hover:bg-hz-bg" aria-label={`Editar ${item.name}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <Switch checked={item.active} onCheckedChange={() => toggle(item)} data-testid={`pricing-toggle-${item.code}`} aria-label={`Activar o desactivar ${item.name}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">Editar precio — {editing?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold">Monto (MXN)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pricing-form-amount" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Descripción visible</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pricing-form-description" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Fecha/horario</Label>
                <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} data-testid="pricing-form-schedule" className="mt-1" placeholder="Ej. Sábado 9:00–18:00" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Qué incluye</Label>
                <Input value={form.includes} onChange={(e) => setForm({ ...form, includes: e.target.value })} data-testid="pricing-form-includes" className="mt-1" />
              </div>
            </div>
            {editing?.code === "child_care_fee" && (
              <div className="space-y-3 rounded-lg border border-hz-gray/40 p-3" data-testid="pricing-childcare-fields">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Edad mín.</Label>
                    <Input type="number" min="0" value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} data-testid="pricing-form-min-age" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Edad máx.</Label>
                    <Input type="number" min="0" value={form.max_age} onChange={(e) => setForm({ ...form, max_age: e.target.value })} data-testid="pricing-form-max-age" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Capacidad</Label>
                    <Input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="pricing-form-capacity" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Mensaje de requisitos</Label>
                  <Input value={form.requirements_message} onChange={(e) => setForm({ ...form, requirements_message: e.target.value })} data-testid="pricing-form-requirements" className="mt-1" placeholder="Ej. Foto verificada y adulto autorizado" />
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
              <Switch checked={form.pending_configuration} onCheckedChange={(v) => setForm({ ...form, pending_configuration: v })} data-testid="pricing-form-pending" />
              Pendiente de configuración
            </label>
            <Button onClick={save} data-testid="pricing-form-save" className="w-full bg-hz-navy font-bold text-white">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edición de datos operativos del asistente (nombre, contacto, iglesia, talleres del registro).
const AttendeeEditDialog = ({ attendee, onClose, onSaved }) => {
  const [catalog, setCatalog] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: attendee.full_name || "", email: attendee.email || "", phone: attendee.phone || "",
    delegation_id: attendee.delegation_id || "", sector_id: attendee.sector_id || "",
    workshop_ids: attendee.workshop_ids || [],
  });

  useEffect(() => {
    api.get("/public/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/admin/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch(() => {});
    api.get(`/admin/attendees/${attendee.id}/entitlements`).then((r) => {
      const active = (r.data.entitlements || []).filter((e) => e.access_type === "workshop" && e.status === "active").map((e) => e.workshop_id);
      setForm((f) => ({ ...f, workshop_ids: f.workshop_ids?.length ? f.workshop_ids : active }));
    }).catch(() => {});
  }, [attendee.id]);

  const sectors = (catalog?.delegations || []).find((d) => d.id === form.delegation_id)?.sectors || [];
  const toggleWs = (id) => setForm((f) => ({
    ...f, workshop_ids: f.workshop_ids.includes(id) ? f.workshop_ids.filter((w) => w !== id) : [...f.workshop_ids, id],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/attendees/${attendee.id}`, {
        full_name: form.full_name, email: form.email, phone: form.phone,
        delegation_id: form.delegation_id || null, sector_id: form.sector_id || null,
        workshop_ids: form.workshop_ids,
      });
      toast.success("Datos del asistente actualizados");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] overflow-y-auto bg-white sm:max-w-lg" data-testid="attendee-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-bold text-hz-navy">Editar datos — {attendee.folio}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs font-semibold">Nombre completo</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="attendee-edit-name-input" className="mt-1" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold">Correo</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="attendee-edit-email-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Teléfono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="attendee-edit-phone-input" className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold">Delegación</Label>
              <select value={form.delegation_id} onChange={(e) => setForm({ ...form, delegation_id: e.target.value, sector_id: "" })}
                data-testid="attendee-edit-delegation-select" className="mt-1 w-full rounded-md border border-hz-gray/50 bg-white px-3 py-2 text-sm">
                <option value="">Selecciona…</option>
                {(catalog?.delegations || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Sector</Label>
              <select value={form.sector_id} onChange={(e) => setForm({ ...form, sector_id: e.target.value })}
                data-testid="attendee-edit-sector-select" className="mt-1 w-full rounded-md border border-hz-gray/50 bg-white px-3 py-2 text-sm">
                <option value="">Selecciona…</option>
                {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Talleres del registro (no modifica accesos ya pagados)</Label>
            <div className="mt-1 space-y-1">
              {workshops.map((w) => (
                <label key={w.id} className="flex items-center gap-2 text-xs font-semibold text-hz-ink">
                  <Checkbox checked={form.workshop_ids.includes(w.id)} onCheckedChange={() => toggleWs(w.id)}
                    data-testid={`attendee-edit-workshop-${w.id}`} aria-label={`Taller ${w.name}`} />
                  {w.name}
                </label>
              ))}
              {workshops.length === 0 && <p className="text-xs text-hz-ink/50">Sin talleres disponibles.</p>}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="attendee-edit-cancel-btn" className="flex-1 font-semibold">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="attendee-edit-save-btn" className="flex-1 bg-hz-navy font-bold text-white hover:bg-hz-blue">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Guardar cambios
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Auditoría del asistente (roles con permiso attendees.view_audit).
const AttendeeAuditList = ({ attendeeId }) => {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    api.get("/admin/audit", { params: { entity_type: "attendee", entity_id: attendeeId } })
      .then((r) => setLogs((r.data.logs || []).slice(0, 15)))
      .catch(() => setLogs([]));
  }, [attendeeId]);
  if (!logs || logs.length === 0) return null;
  return (
    <div className="border-t border-hz-gray/40 pt-3" data-testid="attendee-audit-list">
      <p className="text-xs font-bold text-hz-navy">Auditoría reciente</p>
      <ul className="mt-1 space-y-1 text-xs text-hz-ink/70">
        {logs.map((l) => (
          <li key={l.id} data-testid={`attendee-audit-${l.id}`}>
            <span className="font-semibold text-hz-ink">{l.action}</span> · {l.user_name || "sistema"} · {new Date(l.created_at).toLocaleString("es-MX")}
          </li>
        ))}
      </ul>
    </div>
  );
};

const AttendeesTab = ({ readOnly, role }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [impact, setImpact] = useState(null);
  const [impactFor, setImpactFor] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [acting, setActing] = useState(false);
  const canManage = role === "admin" || role === "welcome_coordinator";
  const isAdmin = role === "admin";
  const [editing, setEditing] = useState(null);
  const [total, setTotal] = useState(0);
  const [testOnly, setTestOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [allFiltered, setAllFiltered] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkAck, setBulkAck] = useState(false);
  const bulkKeyRef = useRef(null);

  const clearSelection = useCallback(() => { setSelected(new Set()); setAllFiltered(false); setPreview(null); }, []);

  const load = useCallback((query = "", st = statusFilter, onlyTest = testOnly) => {
    setLoading(true);
    const params = {};
    if (query) params.q = query;
    if (st === "cancelled") params.status = "cancelled";
    if (onlyTest) params.test_only = true;
    api.get("/admin/attendees", { params })
      .then((r) => { setAttendees(r.data.attendees || []); setTotal(r.data.total ?? (r.data.attendees || []).length); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [statusFilter, testOnly]);

  // Cambios de filtros, búsqueda o página limpian la selección (regla de alcance explícito).
  useEffect(() => { clearSelection(); load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/attendees/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openImpact = async (targetType, targetId) => {
    try {
      const { data } = await api.get("/admin/registrations/impact", { params: { target_type: targetType, target_id: targetId } });
      setImpact(data);
      setImpactFor({ targetType, targetId });
      setCancelReason("");
      setDeleteConfirm("");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doCancel = async () => {
    if (acting) return;
    setActing(true);
    try {
      await api.post("/admin/registrations/cancel", { target_type: impactFor.targetType, target_id: impactFor.targetId, reason: cancelReason });
      toast.success("Registro cancelado. Se revocaron los accesos y el QR.");
      setImpact(null);
      setDetail(null);
      load(q);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  const doDelete = async () => {
    if (acting) return;
    setActing(true);
    try {
      await api.post("/admin/registrations/delete", { target_type: impactFor.targetType, target_id: impactFor.targetId, confirmation_text: deleteConfirm });
      toast.success("Registro de prueba eliminado físicamente.");
      setImpact(null);
      setDetail(null);
      load(q);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : apiError(err));
    } finally {
      setActing(false);
    }
  };

  const selectionPayload = () => allFiltered
    ? { select_all_filtered: true, q: q || null, status: statusFilter === "cancelled" ? "cancelled" : null, test_only: testOnly }
    : { attendee_ids: [...selected] };

  const toggleOne = (id) => {
    setAllFiltered(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const pageIds = attendees.map((a) => a.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const togglePage = () => {
    setAllFiltered(false);
    setSelected((prev) => allPageSelected
      ? new Set([...prev].filter((id) => !pageIds.includes(id)))
      : new Set([...prev, ...pageIds]));
  };

  const selectAllResults = () => {
    setAllFiltered(true);
    setSelected(new Set(pageIds));
  };

  // Vista previa de elegibilidad (debounce) para los contadores de la barra de acciones.
  useEffect(() => {
    if (!isAdmin || (selected.size === 0 && !allFiltered)) { setPreview(null); return; }
    const t = setTimeout(() => {
      setPreviewLoading(true);
      api.post("/admin/attendees/bulk-delete/preview", selectionPayload())
        .then((r) => setPreview(r.data))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, allFiltered]);

  const openBulk = () => { setBulkConfirm(""); setBulkAck(false); setBulkResult(null); bulkKeyRef.current = crypto.randomUUID(); setBulkOpen(true); };

  const expectedPhrase = preview ? `ELIMINAR ${preview.units_count} REGISTROS SELECCIONADOS` : "";

  const doBulkDelete = async () => {
    if (bulkBusy || !preview) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/attendees/bulk-delete/execute", {
        ...selectionPayload(),
        confirmation_text: bulkConfirm,
        acknowledged: bulkAck,
        idempotency_key: bulkKeyRef.current,
        selection_hash: preview.selection_hash,
      });
      setBulkResult(data.result);
      clearSelection();
      load(q);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="attendees-tab">
      <form onSubmit={(e) => { e.preventDefault(); load(q); }} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, folio, correo o teléfono" data-testid="attendees-search-input" />
        <Button type="submit" variant="outline" data-testid="attendees-search-btn" className="font-bold text-hz-navy">
          <Search className="h-4 w-4" />
        </Button>
      </form>
      {canManage && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="attendees-status-filter">
          <button onClick={() => setStatusFilter("active")} data-testid="attendees-filter-active"
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${statusFilter === "active" ? "bg-hz-navy text-white" : "bg-white text-hz-navy border border-hz-gray/50"}`}>
            Activos
          </button>
          <button onClick={() => setStatusFilter("cancelled")} data-testid="attendees-filter-cancelled"
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${statusFilter === "cancelled" ? "bg-hz-navy text-white" : "bg-white text-hz-navy border border-hz-gray/50"}`}>
            Cancelados/archivados
          </button>
          {isAdmin && (
            <button onClick={() => setTestOnly((v) => !v)} data-testid="attendees-filter-test-only"
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${testOnly ? "bg-hz-error text-white" : "bg-white text-hz-error border border-hz-error/50"}`}>
              Solo datos de prueba{testOnly ? ` (${total})` : ""}
            </button>
          )}
        </div>
      )}
      {isAdmin && allPageSelected && !allFiltered && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-hz-cyan/40 bg-hz-cyan/5 px-4 py-2" data-testid="bulk-page-notice">
          <p className="text-xs font-semibold text-hz-ink">
            Se seleccionaron {selected.size} registros de esta página.
            {total > attendees.length ? ` ¿Deseas seleccionar los ${total} registros que coinciden con los filtros actuales?` : ""}
          </p>
          {total > attendees.length && (
            <button onClick={selectAllResults} data-testid="bulk-select-all-filtered-btn" className="text-xs font-bold text-hz-blue underline underline-offset-2">
              Seleccionar los {total} registros filtrados
            </button>
          )}
        </div>
      )}
      {isAdmin && (selected.size > 0 || allFiltered) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border-2 border-hz-error/30 bg-hz-error/5 px-4 py-3" data-testid="bulk-action-bar">
          <p className="text-sm font-bold text-hz-navy" data-testid="bulk-selected-count">
            {allFiltered ? `${total} registros filtrados seleccionados` : `${selected.size} registros seleccionados`}
          </p>
          {previewLoading ? <Loader2 className="h-4 w-4 animate-spin text-hz-navy" /> : preview && (
            <p className="text-xs font-semibold text-hz-ink/70" data-testid="bulk-units-counts">
              {preview.units_count} unidades seleccionadas · {preview.impact.adults} adultos incluidos · {preview.impact.children} niños incluidos
            </p>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} data-testid="bulk-clear-selection-btn" className="font-semibold">Limpiar selección</Button>
            <Button size="sm" onClick={openBulk} disabled={!preview || preview.units_count === 0} data-testid="bulk-delete-open-btn"
              className="bg-hz-error font-bold text-white hover:bg-hz-error/90">
              <Trash2 className="mr-1 h-4 w-4" /> Eliminar registros seleccionados
            </Button>
          </div>
          {allFiltered && (
            <p className="w-full text-xs text-hz-ink/60" data-testid="bulk-filters-note">
              Alcance: todos los registros que coinciden con los filtros actuales{q ? ` (búsqueda: "${q}")` : ""}{statusFilter === "cancelled" ? " · cancelados/archivados" : ""}{testOnly ? " · solo datos de prueba" : ""}.
            </p>
          )}
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : attendees.length === 0 ? (
        <p className="py-8 text-sm text-hz-ink/60" data-testid="attendees-empty">Aún no hay pre-registros.</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-hz-gray/40 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {isAdmin && (
                  <TableHead className="w-10">
                    <Checkbox checked={allPageSelected} onCheckedChange={togglePage} aria-label="Seleccionar todos los registros de esta página" data-testid="bulk-select-page-checkbox" />
                  </TableHead>
                )}
                <TableHead className="font-bold text-hz-navy">Folio</TableHead>
                <TableHead className="font-bold text-hz-navy">Nombre</TableHead>
                <TableHead className="font-bold text-hz-navy">Contacto</TableHead>
                <TableHead className="font-bold text-hz-navy">Ubicación</TableHead>
                <TableHead className="font-bold text-hz-navy">Pago</TableHead>
                <TableHead className="font-bold text-hz-navy">Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendees.map((a) => (
                <TableRow key={a.id} onClick={() => openDetail(a.id)} className={`cursor-pointer hover:bg-hz-bg ${selected.has(a.id) ? "bg-hz-cyan/5" : ""}`} data-testid={`attendee-row-${a.folio}`} aria-selected={selected.has(a.id)}>
                  {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleOne(a.id)} aria-label={`Seleccionar registro ${a.folio}`} data-testid={`attendee-select-${a.folio}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-bold text-hz-navy">{a.folio}</TableCell>
                  <TableCell className="font-semibold">{a.full_name}</TableCell>
                  <TableCell className="text-xs">{a.email}<br />{a.phone}</TableCell>
                  <TableCell className="text-xs">{a.delegation_name} · {a.sector_name}</TableCell>
                  <TableCell><StatusBadge status={a.payment_status} type="payment" /></TableCell>
                  <TableCell className="text-xs">{new Date(a.created_at).toLocaleDateString("es-MX")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] overflow-y-auto overflow-x-hidden bg-white sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy" data-testid="attendee-detail-folio">{detail?.folio}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {detail.photo_url && (
                <PhotoThumb src={detail.photo_url} alt={`Fotografía de ${detail.full_name}`} className="h-24 w-24 rounded-xl border border-hz-gray/50 object-cover" testId="attendee-detail-photo" />
              )}
              <div className="text-sm">
                <p className="font-bold text-hz-ink">{detail.full_name}</p>
                <p className="text-hz-ink/70">{detail.email} · {detail.phone}</p>
              </div>
              <RegistrationSummary data={detail} showPhoto={false} />
              {!readOnly && <EntitlementsPanel attendeeId={detail.id} role={role} />}
              {!readOnly && (
                <Button variant="outline" onClick={() => setEditing(detail)} data-testid="attendee-edit-btn" className="w-full font-bold text-hz-navy">
                  Editar datos operativos
                </Button>
              )}
              {role !== "viewer" && <AttendeeAuditList attendeeId={detail.id} />}
              {canManage && detail.registration_status !== "cancelled" && (
                <div className="flex gap-2 border-t border-hz-gray/40 pt-4">
                  <Button variant="outline" onClick={() => openImpact("attendee", detail.id)} data-testid="attendee-manage-btn" className="flex-1 font-bold text-hz-navy">
                    Eliminar / cancelar registro
                  </Button>
                </div>
              )}
              {detail.registration_status === "cancelled" && (
                <p className="rounded-lg border border-hz-warning/40 bg-hz-warning/5 px-3 py-2 text-xs font-semibold text-hz-ink" data-testid="attendee-cancelled-note">
                  Registro cancelado. Accesos revocados; historial conservado.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editing && (
        <AttendeeEditDialog attendee={editing} onClose={() => setEditing(null)} onSaved={() => { setDetail(null); load(q); }} />
      )}

      <Dialog open={!!impact} onOpenChange={(o) => !o && setImpact(null)}>        <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-lg">
          <div className="flex-none border-b border-hz-gray/40 px-4 py-3 pr-12">
            <DialogHeader>
              <DialogTitle className="font-bold text-hz-navy" data-testid="impact-title">
                {impact?.label} — {impact?.folio}
              </DialogTitle>
              <p className="text-xs font-semibold text-hz-ink/70">{impact?.name}</p>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
            {impact && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-xs [&>div]:min-w-0 [&>div]:break-words" data-testid="impact-summary">
                  <div className="rounded-lg bg-hz-bg px-3 py-2"><span className="font-bold text-hz-navy">{impact.impact.payments}</span> pago(s)/comprobante(s)</div>
                  <div className="rounded-lg bg-hz-bg px-3 py-2"><span className="font-bold text-hz-navy">{impact.impact.active_entitlements}</span> acceso(s) activo(s)</div>
                  <div className="rounded-lg bg-hz-bg px-3 py-2"><span className="font-bold text-hz-navy">{impact.impact.check_ins}</span> check-in(s)</div>
                  <div className="rounded-lg bg-hz-bg px-3 py-2"><span className="font-bold text-hz-navy">{impact.impact.care_movements}</span> mov. cuidado infantil</div>
                  <div className="rounded-lg bg-hz-bg px-3 py-2"><span className="font-bold text-hz-navy">{impact.impact.incidents}</span> incidencia(s)</div>
                  <div className="rounded-lg bg-hz-bg px-3 py-2"><span className="font-bold text-hz-navy">{impact.children_count}</span> niño(s) vinculado(s)</div>
                </div>
                {impact.member_folios?.length > 1 && (
                  <p className="text-xs text-hz-ink/70">Folios individuales: <span className="font-bold text-hz-navy">{impact.member_folios.join(" · ")}</span></p>
                )}
                <div className="rounded-xl border border-hz-warning/40 bg-hz-warning/5 p-3" data-testid="cancel-section">
                  <p className="text-xs font-bold text-hz-ink">Cancelar / archivar (conserva historial, revoca accesos y QR)</p>
                  <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2}
                    placeholder="Motivo obligatorio (mínimo 5 caracteres)" data-testid="cancel-reason-input" className="mt-2" />
                  <Button onClick={doCancel} disabled={acting || cancelReason.trim().length < 5} data-testid="cancel-confirm-btn"
                    className="mt-2 w-full bg-hz-warning font-bold text-white hover:bg-hz-warning/90">
                    {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Cancelar registro
                  </Button>
                </div>
                {role === "admin" && (
                  <div className="rounded-xl border border-hz-error/40 bg-hz-error/5 p-3" data-testid="delete-section">
                    <p className="text-xs font-bold text-hz-error">Eliminación física (solo registros de prueba sin dependencias)</p>
                    {impact.blockers.length > 0 ? (
                      <ul className="mt-2 space-y-1 text-xs text-hz-ink/70" data-testid="delete-blockers">
                        {impact.blockers.map((b, i) => <li key={i}>• {b}</li>)}
                      </ul>
                    ) : (
                      <>
                        <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
                          placeholder='Escribe: ELIMINAR REGISTRO' data-testid="delete-confirm-input" className="mt-2" />
                        <Button onClick={doDelete} disabled={acting || deleteConfirm.trim() !== "ELIMINAR REGISTRO"} data-testid="delete-confirm-btn"
                          className="mt-2 w-full bg-hz-error font-bold text-white hover:bg-hz-error/90">
                          {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Eliminar permanentemente
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o && !bulkBusy) setBulkOpen(false); }}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-2xl" data-testid="bulk-delete-modal">
          <div className="flex-none border-b border-hz-gray/40 px-4 py-3 pr-12">
            <DialogHeader>
              <DialogTitle className="font-bold text-hz-error">Eliminación masiva permanente</DialogTitle>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
            {bulkResult ? (
              <div className="space-y-3" data-testid="bulk-result">
                <p className="text-sm font-bold text-hz-navy">Resultado de la eliminación</p>
                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="bulk-result-deleted">{bulkResult.deleted_units}</span>unidades eliminadas</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-error" data-testid="bulk-result-errors">{bulkResult.errors?.length || 0}</span>errores</div>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <ul className="rounded-lg border border-hz-error/30 bg-hz-error/5 p-3 text-xs text-hz-ink/80" data-testid="bulk-result-error-list">
                    {bulkResult.errors.map((e, i) => <li key={i}>• {e.folio}: {e.error}</li>)}
                  </ul>
                )}
                <Button onClick={() => setBulkOpen(false)} data-testid="bulk-result-close-btn" className="w-full bg-hz-navy font-bold text-white">Cerrar</Button>
              </div>
            ) : !preview ? (
              <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Calculando impacto…</div>
            ) : (
              <div className="space-y-4">
                <p className="rounded-lg border-2 border-hz-error/50 bg-hz-error/5 px-3 py-2 text-xs font-bold text-hz-error" data-testid="bulk-warning">
                  Esta acción eliminará permanentemente los registros seleccionados y toda su información relacionada. No podrá deshacerse.
                </p>
                <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4" data-testid="bulk-preview-units">
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="bulk-units-total">{preview.units_count}</span>unidades</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="bulk-units-individual">{preview.by_type.individual}</span>individuales</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="bulk-units-family">{preview.by_type.family}</span>familias</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="bulk-units-group">{preview.by_type.group}</span>grupos</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4" data-testid="bulk-preview-impact">
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.adults}</b> adulto(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.children}</b> niño(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.payments}</b> pago(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.receipts}</b> comprobante(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.entitlements}</b> derecho(s)/QR</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.workshop_regs}</b> taller(es)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.check_ins}</b> check-in(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.care_movements}</b> mov. cuidado infantil</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.incidents}</b> incidencia(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.notifications}</b> notificaciones/enlaces</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.files}</b> archivo(s) asociado(s)</div>
                </div>
                {preview.units.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-hz-navy">Unidades que se eliminarán (tipo · folio · contacto · pago · registro)</p>
                    <ul className="mt-1 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-hz-gray/30 p-2 text-xs" data-testid="bulk-units-list">
                      {preview.units.map((u) => (
                        <li key={u.unit_id} className="flex flex-wrap justify-between gap-2">
                          <span className="font-bold text-hz-navy">{u.folio}</span>
                          <span className="text-hz-ink/70">{u.label} · {u.name} · pago: {u.payment_status || "—"} · {u.registration_status || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="rounded-lg border border-hz-warning/40 bg-hz-warning/5 px-3 py-2 text-xs font-semibold text-hz-ink" data-testid="bulk-approved-warning">
                  Si los registros tienen pagos aprobados, comprobantes, QR utilizados o asistencias, también se eliminarán permanentemente.
                </p>
                <div>
                  <Label className="text-xs font-bold text-hz-ink">Para confirmar, escribe exactamente: <span className="text-hz-error">{expectedPhrase}</span></Label>
                  <Input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} placeholder={expectedPhrase}
                    data-testid="bulk-confirm-input" className="mt-1.5" aria-label="Frase de confirmación de eliminación masiva" />
                </div>
                <label className="flex items-start gap-2 rounded-lg border border-hz-gray/40 p-3 text-xs font-semibold text-hz-ink" data-testid="bulk-ack-label">
                  <Checkbox checked={bulkAck} onCheckedChange={(c) => setBulkAck(!!c)} data-testid="bulk-ack-checkbox" aria-label="Confirmación de eliminación permanente" />
                  Entiendo que los pagos, QR, asistencias y comprobantes relacionados se eliminarán permanentemente.
                </label>
              </div>
            )}
          </div>
          {!bulkResult && (
            <div className="flex flex-none flex-wrap gap-2 border-t border-hz-gray/40 px-4 py-3">
              <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy} data-testid="bulk-cancel-btn" className="flex-1 font-semibold">Cancelar</Button>
              <Button onClick={doBulkDelete} disabled={bulkBusy || !preview || preview.units_count === 0 || !bulkAck || bulkConfirm.trim() !== expectedPhrase}
                data-testid="bulk-execute-btn" className="flex-1 bg-hz-error font-bold text-white hover:bg-hz-error/90">
                {bulkBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
                Eliminar permanentemente {preview?.units_count || 0} registros
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [delegations, setDelegations] = useState([]);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("hz_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("hz_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/admin/catalog/delegations").then((r) => setDelegations(r.data.items || [])).catch(() => {});
    api.get("/admin/catalog/sectors").then((r) => setSectors(r.data.items || [])).catch(() => {});
  }, [user]);

  const logout = () => {
    localStorage.removeItem("hz_admin_token");
    navigate("/login");
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hz-bg text-hz-navy">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login?redirect=/admin" replace />;

  if (!["admin", "viewer", "welcome_coordinator", "payment_reviewer"].includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-hz-bg px-4 text-center" data-testid="admin-no-perms">
        <p className="text-lg font-bold text-hz-navy">Tu rol no tiene acceso al panel de administración.</p>
        <p className="mt-1 text-sm text-hz-ink/60">Rol actual: {user.role}</p>
        <div className="mt-4 flex gap-3">
          <Button onClick={() => navigate("/checkin")} data-testid="admin-goto-checkin-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">Ir a check-in</Button>
          <Button onClick={logout} variant="outline" data-testid="admin-relogin-btn" className="font-semibold">Cerrar sesión</Button>
        </div>
      </div>
    );
  }

  const readOnly = user.role === "viewer";
  const isCoordinator = user.role === "welcome_coordinator";
  const isReviewer = user.role === "payment_reviewer";
  const initialTab = searchParams.get("tab") || (isReviewer ? "pagos" : "resumen");

  return (
    <div className="min-h-screen bg-hz-bg" data-testid="admin-dashboard">
      <header className="border-b border-hz-gray/50 bg-white">
        <div className="mx-auto flex h-auto min-h-16 max-w-6xl flex-wrap items-center justify-between gap-y-2 px-4 py-2 sm:h-16 sm:px-6 sm:py-0">
          <div className="flex items-center gap-3">
            <Logo variant="title" className="h-8" />
            <span className="rounded-full bg-hz-navy/5 px-3 py-1 text-xs font-bold text-hz-navy">Administración</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span className="hidden text-sm font-medium text-hz-ink/70 sm:block">{user.email}</span>
            {!isReviewer && (
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/asistencia")} data-testid="admin-asistencia-btn" className="font-semibold"><ClipboardCheck className="mr-1 h-4 w-4" />Asistencia</Button>
            )}
            {user.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/admin/landing")} data-testid="admin-landing-btn" className="font-semibold">Landing</Button>
            )}
            {user.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => navigate("/checkin")} data-testid="admin-checkin-btn" className="font-semibold"><ScanLine className="mr-1 h-4 w-4" />Check-in</Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate("/")} data-testid="admin-view-site-btn" className="font-semibold">Ver sitio</Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-btn" className="font-semibold text-hz-error">
              <LogOut className="mr-1 h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <Tabs defaultValue={initialTab}>
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-white p-1">
            {!isReviewer && (
            <TabsTrigger value="resumen" data-testid="tab-resumen" className="font-semibold"><LayoutDashboard className="mr-1.5 h-4 w-4" />Resumen</TabsTrigger>
            )}
            <TabsTrigger value="asistentes" data-testid="tab-asistentes" className="font-semibold"><Users className="mr-1.5 h-4 w-4" />Asistentes</TabsTrigger>
            {!isCoordinator && (
            <TabsTrigger value="pagos" data-testid="tab-pagos" className="font-semibold"><CreditCard className="mr-1.5 h-4 w-4" />Pagos</TabsTrigger>
            )}
            {!readOnly && !isReviewer && (
              <>
            <TabsTrigger value="catalogos" data-testid="tab-catalogos" className="font-semibold"><ListTree className="mr-1.5 h-4 w-4" />Catálogos</TabsTrigger>
            <TabsTrigger value="talleres" data-testid="tab-talleres" className="font-semibold"><Presentation className="mr-1.5 h-4 w-4" />Talleres</TabsTrigger>
            <TabsTrigger value="incidencias" data-testid="tab-incidencias" className="font-semibold"><AlertTriangle className="mr-1.5 h-4 w-4" />Incidencias</TabsTrigger>
              </>
            )}
            <TabsTrigger value="reportes" data-testid="tab-reportes" className="font-semibold"><FileBarChart className="mr-1.5 h-4 w-4" />Reportes</TabsTrigger>
            {!isReviewer && (
            <TabsTrigger value="modulos" data-testid="tab-modulos" className="font-semibold"><Baby className="mr-1.5 h-4 w-4" />Módulos</TabsTrigger>
            )}
            {(user.role === "admin" || isReviewer) && (
            <TabsTrigger value="cena" data-testid="tab-cena" className="font-semibold"><UtensilsCrossed className="mr-1.5 h-4 w-4" />Cena</TabsTrigger>
            )}
            {user.role === "admin" && (
              <>
            <TabsTrigger value="precios" data-testid="tab-precios" className="font-semibold"><Banknote className="mr-1.5 h-4 w-4" />Precios</TabsTrigger>
            <TabsTrigger value="usuarios" data-testid="tab-usuarios" className="font-semibold"><UserCog className="mr-1.5 h-4 w-4" />Usuarios</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config" className="font-semibold"><Settings className="mr-1.5 h-4 w-4" />Config</TabsTrigger>
            <TabsTrigger value="auditoria" data-testid="tab-auditoria" className="font-semibold"><ScrollText className="mr-1.5 h-4 w-4" />Auditoría</TabsTrigger>
              </>
            )}
          </TabsList>

          {!isReviewer && (
          <TabsContent value="resumen" className="mt-6">
            <DashboardTab role={user.role} />
          </TabsContent>
          )}

          <TabsContent value="asistentes" className="mt-6">
            <OperationalCounters source="asistentes" />
            <AttendeesTab readOnly={readOnly} role={user.role} />
          </TabsContent>

          <TabsContent value="pagos" className="mt-6">
            <OperationalCounters source="pagos" />
            <PaymentsTab readOnly={readOnly} role={user.role} />
          </TabsContent>

          {!readOnly && !isReviewer && (
            <>
          <TabsContent value="catalogos" className="mt-6">
            <Tabs defaultValue="delegations">
              <TabsList className="bg-white">
                <TabsTrigger value="delegations" data-testid="subtab-delegaciones" className="font-semibold">Delegaciones</TabsTrigger>
                <TabsTrigger value="sectors" data-testid="subtab-sectores" className="font-semibold">Sectores</TabsTrigger>
              </TabsList>
              <TabsContent value="delegations" className="mt-4">
                <CatalogTab kind="delegations" title="Delegación" />
              </TabsContent>
              <TabsContent value="sectors" className="mt-4">
                <CatalogTab kind="sectors" title="Sector" parents={delegations.filter((d) => d.active)} parentLabel="Delegación" />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="talleres" className="mt-6">
            <WorkshopsTabPro role={user.role} />
          </TabsContent>

          <TabsContent value="incidencias" className="mt-6">
            <IncidentsTab role={user.role} />
          </TabsContent>
            </>
          )}

          <TabsContent value="reportes" className="mt-6">
            <ReportsTab role={user.role} />
          </TabsContent>

          {!isReviewer && (
          <TabsContent value="modulos" className="mt-6">
            <div className="grid gap-4 sm:grid-cols-2" data-testid="modules-tab">
              <Link to="/admin/cuidado-infantil" data-testid="module-childcare-link"
                className="rounded-2xl border-2 border-hz-gray/40 bg-white p-5 transition-all hover:border-hz-cyan hover:shadow-md">
                <Baby className="h-6 w-6 text-hz-cyan" />
                <p className="mt-2 font-bold text-hz-navy">Cuidado infantil</p>
                <p className="mt-1 text-xs text-hz-ink/60">Panel de niños dentro del área, entradas, salidas, alertas e incidencias.</p>
              </Link>
              <Link to="/admin/notificaciones" data-testid="module-notifications-link"
                className="rounded-2xl border-2 border-hz-gray/40 bg-white p-5 transition-all hover:border-hz-cyan hover:shadow-md">
                <Mail className="h-6 w-6 text-hz-cyan" />
                <p className="mt-2 font-bold text-hz-navy">Notificaciones por correo</p>
                <p className="mt-1 text-xs text-hz-ink/60">Historial, plantillas, configuración del proveedor y correos de prueba.</p>
              </Link>
              {user.role === "admin" && (
                <>
              <Link to="/admin/carga-manual" data-testid="module-manual-link"
                className="rounded-2xl border-2 border-hz-gray/40 bg-white p-5 transition-all hover:border-hz-cyan hover:shadow-md">
                <Plus className="h-6 w-6 text-hz-cyan" />
                <p className="mt-2 font-bold text-hz-navy">Carga manual</p>
                <p className="mt-1 text-xs text-hz-ink/60">Alta administrativa de registros con pago confirmado o cortesía.</p>
              </Link>
              <Link to="/admin/importaciones" data-testid="module-import-link"
                className="rounded-2xl border-2 border-hz-gray/40 bg-white p-5 transition-all hover:border-hz-cyan hover:shadow-md">
                <Upload className="h-6 w-6 text-hz-cyan" />
                <p className="mt-2 font-bold text-hz-navy">Importaciones CSV/XLSX</p>
                <p className="mt-1 text-xs text-hz-ink/60">Carga masiva por lotes con validación previa y vista previa.</p>
              </Link>
              <Link to="/admin/herramientas-datos" data-testid="module-datatools-link"
                className="rounded-2xl border-2 border-hz-gray/40 bg-white p-5 transition-all hover:border-hz-cyan hover:shadow-md">
                <AlertTriangle className="h-6 w-6 text-hz-error" />
                <p className="mt-2 font-bold text-hz-navy">Herramientas de datos</p>
                <p className="mt-1 text-xs text-hz-ink/60">Limpieza de datos de prueba y cancelación controlada de registros.</p>
              </Link>
                </>
              )}
            </div>
          </TabsContent>
          )}

          {(user.role === "admin" || isReviewer) && (
          <TabsContent value="cena" className="mt-6">
            <DinnerTab role={user.role} />
          </TabsContent>
          )}

          {user.role === "admin" && (
            <>
          <TabsContent value="precios" className="mt-6">
            <PricingTab />
          </TabsContent>

          <TabsContent value="usuarios" className="mt-6">
            <UsersTab />
          </TabsContent>

          <TabsContent value="config" className="mt-6">
            <SettingsTab />
          </TabsContent>

          <TabsContent value="auditoria" className="mt-6">
            <AuditTab />
          </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;
